/** Query keys for Assets / Issues / Archive sub-tabs under Location Attributes (Room page). */
import { TabRoom, TabRoomType } from 'const/room';

export type RoomTablePagingKey = TabRoomType | 'issuesTab';

/** Resolve which paging/search state bucket is active for the room table area. */
export function resolveRoomTableKey(
  tabTable: number,
  searchParams: URLSearchParams
): RoomTablePagingKey {
  const mainTab = searchParams.get('tab') ?? TabRoom.RoomAttributes;
  if (mainTab === TabRoom.Documentation) return TabRoom.Documentation;
  if (mainTab !== TabRoom.RoomAttributes) return mainTab as TabRoomType;
  return tabTable === 0 ? TabRoom.RoomAttributes : 'issuesTab';
}

const KEYS = {
  page: 'ra_page',
  expand: 'ra_expand',
  subtab: 'ra_subtab'
} as const;

export type RoomAssetToolbarSlice = {
  /** Inner tab: 0 = Assets, 1 = Issues, 2 = Archive photo */
  subtab: number;
  /** Pagination for Assets table (subtab 0 only; kept at 1 when subtab !== 0 in parse). */
  page: number;
  /** Expanded row: submitted asset id (subtab 0 only). */
  expandedSubmittedAssetId: number | undefined;
};

const DEFAULTS: RoomAssetToolbarSlice = {
  subtab: 0,
  page: 1,
  expandedSubmittedAssetId: undefined
};

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw == null || raw === '') return fallback;
  const n = parseInt(raw, 10);
  return !Number.isNaN(n) && n > 0 ? n : fallback;
}

function parseSubtab(raw: string | null): number {
  if (raw == null || raw === '') return DEFAULTS.subtab;
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return DEFAULTS.subtab;
  return Math.min(2, Math.max(0, n));
}

function parseExpandId(raw: string | null): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = parseInt(raw, 10);
  return !Number.isNaN(n) && n > 0 ? n : undefined;
}

/** Read ra_* from URL (Room page, Location Attributes area). */
export function parseRoomAssetToolbarParams(searchParams: URLSearchParams): RoomAssetToolbarSlice {
  const subtab = parseSubtab(searchParams.get(KEYS.subtab));
  const pageOnlyAssets =
    subtab === 0 ? parsePositiveInt(searchParams.get(KEYS.page), DEFAULTS.page) : 1;
  const expand = subtab === 0 ? parseExpandId(searchParams.get(KEYS.expand)) : undefined;
  return {
    subtab,
    page: pageOnlyAssets,
    expandedSubmittedAssetId: expand
  };
}

function deleteRaKeys(params: URLSearchParams): void {
  const toRemove = [...params.keys()].filter((k) => k.startsWith('ra_'));
  toRemove.forEach((k) => params.delete(k));
}

/** Merge toolbar slice into search params (preserves tab, client route, etc.). */
export function mergeRoomAssetToolbarUrl(
  base: URLSearchParams,
  slice: RoomAssetToolbarSlice
): URLSearchParams {
  const next = new URLSearchParams(base);
  deleteRaKeys(next);

  if (slice.subtab !== DEFAULTS.subtab) {
    next.set(KEYS.subtab, String(slice.subtab));
  }
  if (slice.subtab === 0) {
    if (slice.page !== DEFAULTS.page) {
      next.set(KEYS.page, String(slice.page));
    }
    if (slice.expandedSubmittedAssetId != null) {
      next.set(KEYS.expand, String(slice.expandedSubmittedAssetId));
    }
  }
  return next;
}

export function roomAssetToolbarSliceEquals(
  a: RoomAssetToolbarSlice,
  b: RoomAssetToolbarSlice
): boolean {
  return (
    a.subtab === b.subtab &&
    a.page === b.page &&
    a.expandedSubmittedAssetId === b.expandedSubmittedAssetId
  );
}
